type Resources = Record<string, unknown>;

/** The part of an i18next instance `mergePageTranslations` writes through. */
type ResourceBundleStore = {
  addResourceBundle: (
    locale: string,
    namespace: string,
    resources: Resources,
    deep?: boolean,
    overwrite?: boolean
  ) => unknown;
};

/**
 * The `_nextI18Next` page prop `serverSideTranslations` returns, plus the
 * namespaces `pickNamespaceKeys` (`lib/getTranslations.js`) cut down to a few
 * keys for that page.
 */
export type PageTranslations = {
  initialI18nStore?: Record<string, Record<string, Resources>>;
  subsetNamespaces?: string[];
};

// Per i18n instance, the `locale/namespace` bundles that hold the whole
// namespace. A WeakMap, so the server's per-request instances leave nothing
// behind.
const wholeBundles = new WeakMap<object, Set<string>>();

const bundleId = (locale: string, namespace: string) =>
  `${locale}/${namespace}`;

/** Records that `i18n` now holds every key of `namespace` in `locale`. */
export const markWholeBundle = (
  i18n: object,
  locale: string,
  namespace: string
) => {
  const bundles = wholeBundles.get(i18n) ?? new Set<string>();

  bundles.add(bundleId(locale, namespace));
  wholeBundles.set(i18n, bundles);
};

/**
 * Whether `i18n` holds every key of `namespace` in `locale`: a page shipped it
 * whole, or `useLazyNamespace` loaded its chunk. i18next's `hasResourceBundle`
 * cannot tell; it is also true for the 13 `account` keys a product page ships.
 */
export const hasWholeBundle = (
  i18n: object,
  locale: string,
  namespace: string
): boolean => wholeBundles.get(i18n)?.has(bundleId(locale, namespace)) ?? false;

/**
 * Adds a page's server-side translations to the i18n store.
 *
 * next-i18next's `appWithTranslation` adds a page's namespace only while the
 * store has nothing for that namespace and locale, so the first page that
 * brings a namespace decides it for the rest of the visit. A product page
 * ships 13 keys of `account` (WHO-5959 F17); opened first, it left My Account
 * rendering its sidebar as raw keys until a full reload (WHO-6644). Here every
 * page's bundles are deep-merged, the page's strings winning, until the store
 * holds the whole namespace.
 */
export const mergePageTranslations = (
  i18n: ResourceBundleStore,
  translations: PageTranslations | null | undefined
) => {
  if (!translations?.initialI18nStore) {
    return;
  }

  const { initialI18nStore, subsetNamespaces = [] } = translations;
  const subsets = new Set(subsetNamespaces);

  Object.entries(initialI18nStore).forEach(([locale, namespaces]) => {
    Object.entries(namespaces).forEach(([namespace, resources]) => {
      if (hasWholeBundle(i18n, locale, namespace)) {
        return;
      }

      i18n.addResourceBundle(locale, namespace, resources, true, true);

      if (!subsets.has(namespace)) {
        markWholeBundle(i18n, locale, namespace);
      }
    });
  });
};
